import { FormControl, Stack, TextField } from '@mui/material';
import React, { useState,useContext,useEffect,useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch,useSelector } from 'react-redux';
import { ParentScreenCtx } from '/src/pages/workbench_build/WorkbenchBuild';
import {eachDeep} from 'deepdash-es/standalone';
import { debounce,cloneDeep,isEmpty, has, isEqual } from 'lodash';
import { treeActions } from '../../../redux/slices/treeSlice';
import { genericRules, validateOnFlow, validateRevertTime, validationTime } from '../../../utils/validate';
import RecordDataValidation from './RecordDataValidation';
import TPCheckbox from '../../TemplateProperties/TPCheckbox';

function FieldGroupDataValidation(props) {
    const {t} = useTranslation();
    const parentScreen = useContext(ParentScreenCtx);
    const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
    const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
    const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
    const templateHeader = rightTree.tree.templateHeader;
    const dispatch = useDispatch();
    const [minCardinality,setMinCardinality] = useState('');
    const [maxCardinality,setMaxCardinality] = useState('');
    const [maxLength,setMaxLength] = useState('')
    const [fieldGroupReq,setFieldGroupReq] = useState(false);
    const [errors,setErrors] = useState('')
    const errorRef = useRef();

    const validationSchema = {
        MIN_LOOPS: genericRules.numericRule,
        MAX_LOOPS:genericRules.numericRule,
        MAX_LENGTH: genericRules.numericRule,
        MAX_REPETITION: genericRules.numericRuleWithNoLengthLimit
    }
    useEffect(()=>{
        if(selectedNode){
            const editRule = selectedNode?.EDIT_RULE?.length ? selectedNode.EDIT_RULE[0] : ''; // Fixed for Breaking Code
            setMaxCardinality(editRule.MAX_LOOPS || '0');
            setMinCardinality(editRule.MIN_LOOPS || '0');
            setMaxLength(editRule.MAX_LENGTH || '0');
            setFieldGroupReq(editRule.REQUIRED === "TRUE" ? true : false);
        }
    },[selectedNode])

    const minMaxValidation = () =>{
        if(Number(minCardinality) >= Number(maxCardinality)){
          setMaxCardinality(minCardinality);
          debounceChangeHandler('MAX_LOOPS',minCardinality)
        }
    }
    
  const setMinMaxCardinality = ({ value, name },stateFunction) => {
    const errorState = validateOnFlow(name, value, validationSchema);
    const isEmptyObj = isEmpty(errorState);
    stateFunction(value);
    if (isEmptyObj || !value) {
      debounceChangeHandler(name, Number(value))
    }
    setErrors((previous) => {
      let copyPrev = JSON.parse(JSON.stringify(previous));
      if(isEmptyObj)
        delete copyPrev[name];
      else
        copyPrev = {
          ...copyPrev,
          ...errorState
        }
      return copyPrev;
    })

    // Handle Validation Logic
    if(!isEmpty(errorState)){
      setTimeout(()=>{
        stateFunction((previousPrecisionValue) => {
          let val = previousPrecisionValue;
          val = val.replace(/\D/g, '');
          if(val.length > 9){
            val = val.substring(0,9);
          }
          return val;
        })
      },validateRevertTime)
      if(errorRef?.current?.[name]) clearTimeout(errorRef?.current[name]);
      const timerId= setTimeout(() => {
        setErrors((prev) => {
          const copyPrev = JSON.parse(JSON.stringify(prev));
          delete copyPrev[name];
          return copyPrev;
        })
      }, validationTime)
      errorRef.current = {...errorRef.current , [name] : timerId}
      return;
    }
  };
  const submitChange = (key, value) => {
    if (key !== 'MIN_LOOPS' &&
      key !== 'MAX_LOOPS' &&
      key !== 'MAX_LENGTH' &&
      key !== 'REQUIRED'
    ) return;

    let newSelectedNode = cloneDeep(selectedNode);
    const updatedData = eachDeep(cloneDeep(translatedData), (objValue) => {
      if (objValue?.key !== selectedNode.key) {
        return;
      }
      if (objValue.ICON_DISPLAY === 'FIELD_GROUP' || objValue.ICON_DISPLAY === 'REPEATING_FIELD_GROUP') {
        if (objValue.EDIT_RULE) objValue.EDIT_RULE[0][key] = value;
        else {
          objValue.EDIT_RULE = [{}]; 
          objValue.EDIT_RULE[0][key] = value;
        }
      }

      newSelectedNode = objValue;
    });

    //prevent unnecessary dispatch
    if (isEqual(translatedData, updatedData)) return;

    dispatch(
      treeActions.setRightTreeSelectedNode({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: newSelectedNode,
      })
    );

    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );
  }
    const debounceChangeHandler = useCallback(debounce(submitChange, 800), [
        rightTree.currentTab,
        rightTree.tree,
      ]);
  return (
    <>
     {templateHeader?.FILETYPE ==='HL7' && <Stack className='tp-segment' spacing={'10px'}>
      <FormControl>
         <TextField
           variant='outlined'
           size='small'
           autoComplete='off'
           type='text'
           className={'width-100'}
           label={t('LBL_MIN_CARDINALITY')}
           name={'MIN_LOOPS'}
           value={minCardinality}
           onChange={(e)=>{
            setMinMaxCardinality(e.target,setMinCardinality)
           }}
           onBlur={minMaxValidation}
           helperText={errors?.MIN_LOOPS}
           error={has(errors,'MIN_LOOPS')}
         >

         </TextField>
      </FormControl>
      <FormControl>
         <TextField
           variant='outlined'
           size='small'
           autoComplete='off'
           type='text'
           className={'width-100'}
           label={t('LBL_MAX_CARDINALITY')}
           name={'MAX_LOOPS'}
           value={maxCardinality}
           onChange={(e)=>{
            setMinMaxCardinality(e.target,setMaxCardinality)
           }}
           onBlur={minMaxValidation}
           helperText={errors?.MAX_LOOPS}
           error={has(errors,'MAX_LOOPS')}
         >

         </TextField>
      </FormControl>
      <FormControl>
         <TextField
           variant='outlined'
           size='small'
           autoComplete='off'
           type='text'
           className={'width-100'}
           label={t('LBL_MAX_LENGTH')}
           name={'MAX_LENGTH'}
           value={maxLength}
           onChange={(e)=>{
            setMinMaxCardinality(e.target,setMaxLength)
           }}
           onBlur={minMaxValidation}
           helperText={errors?.MAX_LENGTH}
           error={has(errors,'MAX_LENGTH')}
         >

         </TextField>
      </FormControl>
      <FormControl>
        <TPCheckbox
          label={t("BTN_FIELD_GROUP_REQ")}
          checked={fieldGroupReq}
          handleCheck={(checked) => {
            setFieldGroupReq(checked);
            debounceChangeHandler("REQUIRED", checked);
          }}
        />
        </FormControl>
     </Stack>}
     
     {templateHeader?.FILETYPE === 'EDI' && <RecordDataValidation  isVisible={props.isVisible}/>}
    </>
  )
}

export default FieldGroupDataValidation